import React, { useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { buildSosTelUrl, buildTelUrl, buildWhatsappToPhone, buildSmsUrl, openWhatsappChat } from '../utils/support';
import { APP } from '../config/app';
import { triggerRideSos, logRideEvent } from '../services/firebase';
import { hapticSos, hapticSelection } from '../utils/haptics';
import useScrollLock from '../utils/useScrollLock';

export default function SosSheet({ t, rideId, onClose }) {
  const [busy, setBusy] = useState(false);
  const [alerted, setAlerted] = useState(false);
  // The server raises the alert (+ pings ops) on the first call and is
  // idempotent after that, so hit it exactly once per sheet — WhatsApp, SMS
  // and Contacts all reuse this payload instead of each making their own
  // call (which is what was tripping the SOS cooldown and showing an error
  // on the 2nd/3rd channel).
  const sosDataRef = useRef(null);
  useScrollLock();
  const supportTel = buildTelUrl();
  const hasSupport = supportTel !== '#';

  const ensureSosData = async () => {
    if (sosDataRef.current) return sosDataRef.current;
    const data = await triggerRideSos(rideId);
    sosDataRef.current = {
      trackUrl: data?.trackUrl || '',
      contacts: Array.isArray(data?.contacts) ? data.contacts : [],
    };
    return sosDataRef.current;
  };

  // Raise the server alert (ops team + admin SOS inbox) without blocking the
  // dial. Any SOS action — including just calling 112 — must notify MyTownly.
  const raiseServerAlert = () => {
    ensureSosData()
      .then(() => setAlerted(true))
      .catch((e) => console.warn('SOS server alert failed', e?.message));
  };

  const call112 = () => {
    hapticSos();
    logRideEvent(rideId, 'sos_action', { action: 'call_112' });
    raiseServerAlert();
    toast.success(t('sos.teamNotified'));
    window.location.href = buildSosTelUrl();
  };

  const callSupport = () => {
    hapticSos();
    if (!hasSupport) {
      toast.error(t('active.sosUnavailable'));
      return;
    }
    logRideEvent(rideId, 'sos_action', { action: 'call_support' });
    raiseServerAlert();
    window.location.href = supportTel;
  };

  const shareDirectWhatsapp = async () => {
    hapticSelection();
    setBusy(true);
    try {
      const { trackUrl } = await ensureSosData();
      const text = t('sos.directShareMsg', { city: APP.city, track: trackUrl });
      openWhatsappChat(text, '');
      setAlerted(true);
      logRideEvent(rideId, 'sos_action', { action: 'whatsapp_share' });
      toast.success(t('sos.alertedDone'));
    } catch (e) {
      console.error(e);
      toast.error(t('sos.alertFailed'));
    } finally {
      setBusy(false);
    }
  };

  const shareDirectSms = async () => {
    hapticSelection();
    setBusy(true);
    try {
      const { trackUrl } = await ensureSosData();
      const text = t('sos.directShareMsg', { city: APP.city, track: trackUrl });
      const smsUrl = `sms:?body=${encodeURIComponent(text)}`;
      window.location.href = smsUrl;
      setAlerted(true);
      logRideEvent(rideId, 'sos_action', { action: 'sms_share' });
      toast.success(t('sos.alertedDone'));
    } catch (e) {
      console.error(e);
      toast.error(t('sos.alertFailed'));
    } finally {
      setBusy(false);
    }
  };

  const alertContacts = async () => {
    hapticSos();
    if (busy) return;
    setBusy(true);
    try {
      const { contacts, trackUrl } = await ensureSosData();
      if (!contacts.length) {
        const fallbackMsg = encodeURIComponent(t('sos.directShareMsg', { city: APP.city, track: trackUrl }));
        window.open(`https://wa.me/?text=${fallbackMsg}`, '_blank', 'noopener,noreferrer');
        setAlerted(true);
        logRideEvent(rideId, 'sos_action', { action: 'contacts_alerted', contactCount: 0 });
        toast.success(t('sos.alertedDone'));
        return;
      }
      const msg = t('sos.alertMsg', {
        track: trackUrl,
        city: APP.city,
      });
      contacts.forEach((c, i) => {
        const wa = buildWhatsappToPhone(c.phone, msg);
        const sms = buildSmsUrl(c.phone, msg);
        setTimeout(() => {
          if (i === 0) openWhatsappChat(msg, c.phone);
          else if (sms !== '#') window.open(sms, '_blank', 'noopener,noreferrer');
        }, i * 400);
      });
      setAlerted(true);
      logRideEvent(rideId, 'sos_action', { action: 'contacts_alerted', contactCount: contacts.length });
      toast.success(t('sos.alerted', { n: contacts.length }));
    } catch (e) {
      console.error(e);
      toast.error(t('sos.alertFailed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="modal-backdrop"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="modal-sheet sos-sheet"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <h3>{t('sos.title')}</h3>
        <p className="text-muted">{t('sos.subtitle')}</p>

        <button type="button" className="sos-action sos-action-emergency" onClick={call112}>
          <span className="sos-action-ico">🚨</span>
          <span>
            <strong>{t('sos.call112')}</strong>
            <small>{t('sos.call112Sub')}</small>
          </span>
        </button>
        <p className="sos-team-note">{t('sos.teamNote')}</p>

        <button type="button" className="sos-action sos-action-whatsapp" onClick={shareDirectWhatsapp} disabled={busy}>
          <span className="sos-action-ico">💬</span>
          <span>
            <strong>{t('sos.shareWhatsapp')}</strong>
            <small>{t('sos.shareWhatsappSub')}</small>
          </span>
        </button>

        <button type="button" className="sos-action sos-action-sms" onClick={shareDirectSms} disabled={busy}>
          <span className="sos-action-ico">📱</span>
          <span>
            <strong>{t('sos.shareSms')}</strong>
            <small>{t('sos.shareSmsSub')}</small>
          </span>
        </button>

        <button type="button" className="sos-action" onClick={callSupport} disabled={!hasSupport}>
          <span className="sos-action-ico">📞</span>
          <span>
            <strong>{t('sos.callSupport')}</strong>
            <small>{hasSupport ? t('sos.callSupportSub') : t('active.sosUnavailable')}</small>
          </span>
        </button>

        <button type="button" className="sos-action" onClick={alertContacts} disabled={busy}>
          <span className="sos-action-ico">👥</span>
          <span>
            <strong>{alerted ? t('sos.alertedDone') : t('sos.alertContacts')}</strong>
            <small>{t('sos.alertContactsHint')}</small>
          </span>
        </button>

        <button type="button" className="btn btn-ghost" onClick={onClose} style={{ marginTop: 8 }}>
          {t('sos.close')}
        </button>
      </div>
    </div>
  );
}

